import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Divider,
  useTheme,
  useMediaQuery,
  Avatar,
  Tooltip,
  Fade,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import HomeIcon from '@mui/icons-material/Home';
import StarIcon from '@mui/icons-material/Star';
// import ChatIcon from '@mui/icons-material/Chat'; // commented - Demo nav hidden
// import HowToRegIcon from '@mui/icons-material/HowToReg'; // commented - How It Works nav hidden
// import FormatQuoteIcon from '@mui/icons-material/FormatQuote'; // commented - Testimonials nav hidden
import InfoIcon from '@mui/icons-material/Info';
import ContactMailIcon from '@mui/icons-material/ContactMail';
// import AppsIcon from '@mui/icons-material/Apps'; // commented - Products nav hidden
// import CodeIcon from '@mui/icons-material/Code'; // commented - LoCoPilot separate link removed
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import LayersIcon from '@mui/icons-material/Layers';
import { useNavigate, useLocation } from 'react-router-dom';

// Nav links for LoCoPilot page (/) — all sections stay on same page
const navLinks = [
  { label: 'Home', href: '#download', icon: <HomeIcon /> },
  { label: 'Why LoCoPilot', href: '#features', icon: <StarIcon /> },
  { label: 'Models', href: '#models', icon: <LayersIcon /> },
  { label: 'Getting Started', href: '#getting-started', icon: <RocketLaunchIcon /> },
  { label: 'About Us', href: '#about', icon: <InfoIcon /> },
  { label: 'Contact', href: '#contact', icon: <ContactMailIcon /> },
];

const NavBar = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('download');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();

  // Handle scroll effect for navbar
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
      
      // Update active section based on scroll position
      const sections = navLinks.map(link => link.href.replace('#', ''));
      for (const section of sections.reverse()) {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 150) {
            setActiveSection(section);
            break;
          }
        }
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleDrawerToggle = () => {
    setDrawerOpen((prev) => !prev);
  };

  const handleNavClick = (href) => {
    setDrawerOpen(false);

    if (href.startsWith('#')) {
      // All nav hash links target LoCoPilot page sections — stay on /
      if (location.pathname !== '/') {
        navigate('/');
        setTimeout(() => {
          const el = document.querySelector(href);
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            setTimeout(() => {
              el.setAttribute('tabindex', '-1');
              el.focus({ preventScroll: true });
            }, 600);
          }
        }, 100);
      } else {
        const el = document.querySelector(href);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          setTimeout(() => {
            el.setAttribute('tabindex', '-1');
            el.focus({ preventScroll: true });
          }, 600);
        }
      }
    } else {
      navigate(href);
    }
  };

  const isActive = (href) => {
    if (href.startsWith('/')) return location.pathname === href;
    // Hash links: only active on LoCoPilot page (/) when section is in view
    if (location.pathname !== '/') return false;
    return activeSection === href.replace('#', '');
  };

  return (
    <>
      <AppBar
        position="fixed"
        elevation={scrolled ? 4 : 0}
        sx={{
          background: scrolled 
            ? 'rgba(255, 255, 255, 0.92)' 
            : 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          borderBottom: scrolled 
            ? '1px solid rgba(0, 121, 107, 0.12)' 
            : '1px solid rgba(0, 121, 107, 0.05)',
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          zIndex: theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar 
          sx={{ 
            justifyContent: 'space-between', 
            minHeight: { xs: 70, md: scrolled ? 72 : 84 },
            transition: 'min-height 0.3s ease',
            px: { xs: 2, md: 4 },
          }}
        >
          {/* Logo and Brand - click goes to home (top of page) */}
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              cursor: 'pointer',
              transition: 'transform 0.3s ease',
              '&:hover': {
                transform: 'scale(1.02)',
              },
            }} 
            onClick={() => handleNavClick('#download')}
          >
            <Box
              sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, rgba(0, 121, 107, 0.15) 0%, rgba(0, 77, 64, 0.1) 100%)',
                  animation: 'pulse 3s ease-in-out infinite',
                }}
              />
              <Avatar
                src="/Monogram_72_ppi-01.png"
                alt="Bixbite AI Logo"
                sx={{
                  width: 48,
                  height: 48,
                  bgcolor: 'white',
                  boxShadow: '0 4px 20px -4px rgba(0, 121, 107, 0.3)',
                  border: '2px solid',
                  borderColor: 'rgba(0, 121, 107, 0.2)',
                  objectFit: 'contain',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    boxShadow: '0 6px 24px -4px rgba(0, 121, 107, 0.4)',
                  },
                }}
                imgProps={{
                  style: { objectFit: 'contain', background: 'white' },
                }}
              />
            </Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 800,
                letterSpacing: '-0.01em',
                background: 'linear-gradient(135deg, #00796b 0%, #004d40 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                ml: 1.5,
                display: { xs: 'none', sm: 'block' },
                fontSize: { sm: '1.3rem', md: '1.5rem' },
              }}
            >
              Bixbite AI
            </Typography>
          </Box>

          {/* Desktop Nav */}
          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {navLinks.map((link, index) => (
                <Fade in timeout={300 + index * 50} key={link.label}>
                  <Tooltip title={link.label} arrow enterDelay={500}>
                    <Button
                      onClick={() => handleNavClick(link.href)}
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.9rem',
                        px: 1.5,
                        py: 1,
                        borderRadius: '10px',
                        textTransform: 'none',
                        color: isActive(link.href) 
                          ? theme.palette.primary.main 
                          : theme.palette.text.secondary,
                        position: 'relative',
                        overflow: 'hidden',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.8,
                        minWidth: 0,
                        background: isActive(link.href) 
                          ? 'rgba(0, 121, 107, 0.08)' 
                          : 'transparent',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          bottom: 4,
                          left: '50%',
                          transform: isActive(link.href) 
                            ? 'translateX(-50%) scaleX(1)' 
                            : 'translateX(-50%) scaleX(0)',
                          width: '60%',
                          height: '2px',
                          background: 'linear-gradient(90deg, #00796b, #004d40)',
                          borderRadius: '2px',
                          transition: 'transform 0.3s ease',
                        },
                        '&:hover': {
                          bgcolor: 'rgba(0, 121, 107, 0.08)',
                          color: theme.palette.primary.main,
                          transform: 'translateY(-1px)',
                        },
                        '&:hover::before': {
                          transform: 'translateX(-50%) scaleX(1)',
                        },
                      }}
                      aria-label={link.label}
                    >
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          fontSize: 20,
                          color: isActive(link.href) 
                            ? theme.palette.primary.main 
                            : 'inherit',
                          transition: 'color 0.3s ease',
                        }}
                      >
                        {React.cloneElement(link.icon, { fontSize: 'small' })}
                      </Box>
                      <Box 
                        component="span" 
                        sx={{ 
                          display: { xs: 'none', lg: 'inline' },
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {link.label}
                      </Box>
                    </Button>
                  </Tooltip>
                </Fade>
              ))}
            </Box>
          )}

          {/* Mobile Hamburger */}
          {isMobile && (
            <IconButton
              edge="end"
              aria-label="menu"
              onClick={handleDrawerToggle}
              sx={{
                width: 44,
                height: 44,
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #00796b 0%, #004d40 100%)',
                color: 'white',
                boxShadow: '0 4px 14px -4px rgba(0, 121, 107, 0.4)',
                transition: 'all 0.3s ease',
                '&:hover': { 
                  transform: 'scale(1.05)',
                  boxShadow: '0 6px 20px -4px rgba(0, 121, 107, 0.5)',
                },
              }}
            >
              <MenuIcon />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      {/* Spacer for fixed navbar */}
      <Box sx={{ height: { xs: 70, md: 84 } }} />

      {/* Drawer for Mobile */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleDrawerToggle}
        PaperProps={{
          sx: {
            width: 300,
            background: 'linear-gradient(180deg, #ffffff 0%, #f8fffe 100%)',
            borderLeft: '1px solid rgba(0, 121, 107, 0.1)',
          },
        }}
        transitionDuration={300}
      >
        <Box sx={{ p: 3 }}>
          {/* Drawer Header - logo and brand click go to home */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                '&:hover': { opacity: 0.85 },
              }}
              onClick={() => handleNavClick('#download')}
            >
              <Avatar
                src="/Monogram_72_ppi-01.png"
                alt="Bixbite AI Logo"
                sx={{
                  width: 44,
                  height: 44,
                  bgcolor: 'white',
                  boxShadow: '0 4px 16px -4px rgba(0, 121, 107, 0.3)',
                  border: '2px solid rgba(0, 121, 107, 0.15)',
                  mr: 1.5,
                }}
                imgProps={{
                  style: { objectFit: 'contain', background: 'white' },
                }}
              />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 800,
                  background: 'linear-gradient(135deg, #00796b 0%, #004d40 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                Bixbite AI
              </Typography>
            </Box>
            <IconButton
              onClick={handleDrawerToggle}
              sx={{
                bgcolor: 'rgba(0, 121, 107, 0.08)',
                '&:hover': { bgcolor: 'rgba(0, 121, 107, 0.15)' },
              }}
            >
              <CloseIcon sx={{ color: theme.palette.primary.main }} />
            </IconButton>
          </Box>

          <Divider sx={{ mb: 2, borderColor: 'rgba(0, 121, 107, 0.1)' }} />

          <List sx={{ px: 0 }}>
            {navLinks.map((link, index) => (
              <Fade in timeout={200 + index * 50} key={link.label}>
                <ListItem disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    onClick={() => handleNavClick(link.href)}
                    sx={{
                      borderRadius: '12px',
                      py: 1.5,
                      px: 2,
                      transition: 'all 0.3s ease',
                      background: isActive(link.href) 
                        ? 'linear-gradient(135deg, rgba(0, 121, 107, 0.1) 0%, rgba(0, 77, 64, 0.05) 100%)'
                        : 'transparent',
                      borderLeft: isActive(link.href) 
                        ? '3px solid #00796b' 
                        : '3px solid transparent',
                      '&:hover': {
                        background: 'linear-gradient(135deg, rgba(0, 121, 107, 0.08) 0%, rgba(0, 77, 64, 0.04) 100%)',
                        transform: 'translateX(4px)',
                      },
                    }}
                  >
                    <ListItemIcon 
                      sx={{ 
                        minWidth: 40,
                        color: isActive(link.href) 
                          ? theme.palette.primary.main 
                          : theme.palette.text.secondary,
                      }}
                    >
                      {React.cloneElement(link.icon, { 
                        sx: { fontSize: 22 } 
                      })}
                    </ListItemIcon>
                    <ListItemText
                      primary={link.label}
                      primaryTypographyProps={{
                        fontWeight: isActive(link.href) ? 700 : 600,
                        fontSize: '1rem',
                        color: isActive(link.href) 
                          ? theme.palette.primary.main 
                          : theme.palette.text.primary,
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              </Fade>
            ))}
          </List>
        </Box>
      </Drawer>
    </>
  );
};

export default NavBar;
